import React, { useState, useEffect, useContext, useRef } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { sGetAuthLoaded } from '@/store/reducers/auth';

import Table from './Table';
import Box from '../../atomic/Box';
import TableLoading from '../../desktop/loaders/TableLoading';
import DocumentActionMenu from './DocumentActionMenu';
import { FileUploadContext } from '../../../contexts/fileUpload';

import {
  getDefaultDocumentSortType,
  getDocumentId,
  getDocumentSortType,
  getFilteredDocuments,
} from '../../../utils/documents';
import { areIdsEqual } from '../../../utils/strings';
import useDocumentFilters from '../../../hooks/useDocumentFilters';

import matomo from '../../../utils/matomo';

const DocumentViewTable = ({
  authLoaded,
  fetching,
  documents = [],
  onFolderChange,
  documentFolders,
  directoryId,
  setDirectoryId = () => {},
  directoryTree,
  setDirectoryTree = () => {},
  searchQuery,
  onDocumentDeleted,
  publicSection = false,
  actionsEnabled = true,
  showBulkSelectionOptions,
  isPersonalDocuments = false,
  isFromPersonal,
  isBoligmappaPlusUser = false,
  onFolderDelete,
}) => {
  const { filters, onFiltersChanged } = useDocumentFilters(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDocumentId, setCurrentDocumentId] = useState(null);
  const [sortType, setSortType] = useState(getDefaultDocumentSortType());
  const [sortingDirection, setSortingDirection] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [scrollPosition, setScrollPosition] = useState(0);

  const tableContainerRef = useRef(null);

  const { files } = useContext(FileUploadContext);

  const currentDocument = (documents || [])
    .concat(documentFolders)
    .find((document) =>
      areIdsEqual(getDocumentId(document), currentDocumentId)
    );

  useEffect(() => {
    if (files.length > 0) {
      setUploadedFiles(files);
      window.scrollTo(0, 0);
    }
  }, [files]);

  const filteredDocuments = getFilteredDocuments({
    filters,
    documents,
    documentFolders,
    searchQuery,
    shouldSort: true,
    sortFunction: sortType.sortFunction,
    isPersonalDocuments,
  });

  useEffect(() => {
    if (searchQuery) {
      matomo.clickEvent({
        category: 'Document handling',
        action: 'Search for Document',
        name: 'Search for Document',
        value: filteredDocuments?.length,
      });
    }
  }, [searchQuery]);

  const onDocumentClick = (event, document) => {
    event.persist();
    setCurrentDocumentId(getDocumentId(document));
    setAnchorEl(event.currentTarget);
  };

  const onSortTypeChange = (newSortType) => {
    setSortType(getDocumentSortType(newSortType));
  };

  const toggleContextMenu = (open) => {
    if (!open) {
      setAnchorEl(null);
      setCurrentDocumentId(null);
    }
  };
  const handleScroll = () => {
    if (tableContainerRef.current !== null) {
      setScrollPosition(window.scrollY);
    }
  };

  const debouncedHandleScroll = debounce(handleScroll, 300);

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll);

    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, []);

  useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!fetching && authLoaded && tableContainerRef.current !== null) {
      window.scrollTo(0, scrollPosition);
    }
  }, [fetching, authLoaded, tableContainerRef.current]);

  useEffect(() => {
    const savedScrollPosition = localStorage.getItem('scrollPosition');
    const savedSortType = JSON.parse(localStorage.getItem('sortType'));

    if (savedSortType !== null) {
      setSortType(getDocumentSortType(savedSortType.id));
      setSortingDirection(savedSortType.id);
    }
    setScrollPosition(parseInt(savedScrollPosition, 10));
  }, []);

  useEffect(() => {
    localStorage.setItem('sortType', JSON.stringify(sortType));
  }, [sortType]);

  useEffect(() => {
    localStorage.setItem('scrollPosition', scrollPosition.toString());
  }, [scrollPosition]);

  if (fetching) {
    return (
      <Box pt={2}>
        <TableLoading rows={5} columns={4} />
      </Box>
    );
  }
  return (
    <Box>
      <Table
        filters={filters}
        sortType={sortType}
        documents={documents}
        onFolderChange={onFolderChange}
        directoryId={directoryId}
        setDirectoryId={setDirectoryId}
        directoryTree={directoryTree}
        setDirectoryTree={setDirectoryTree}
        searchQuery={searchQuery}
        publicSection={publicSection}
        actionsEnabled={actionsEnabled}
        tableContainerRef={tableContainerRef}
        onDocumentClick={onDocumentClick}
        onFiltersChanged={onFiltersChanged}
        onSortTypeChange={onSortTypeChange}
        filteredDocuments={filteredDocuments}
        sortingDirection={sortingDirection}
        setSortingDirection={setSortingDirection}
        showBulkSelectionOptions={showBulkSelectionOptions}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
        isPersonalDocumentView={isPersonalDocuments}
        isFromPersonal={isFromPersonal}
        isBoligmappaPlusUser={isBoligmappaPlusUser}
      />

      <DocumentActionMenu
        full={false}
        parentAnchorEl={anchorEl}
        document={currentDocument}
        setOpenParentPop={toggleContextMenu}
        onDocumentDeleted={onDocumentDeleted}
        openParentPop={!!anchorEl && !!currentDocument}
        documentId={getDocumentId(currentDocument)}
        isPersonalDocuments={isPersonalDocuments}
        isFromPersonal={isFromPersonal}
        isBoligmappaPlusUser={isBoligmappaPlusUser}
        onFolderDelete={onFolderDelete}
        onFolderChange={onFolderChange}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  authLoaded: sGetAuthLoaded(state),
});

export default connect(mapStateToProps)(DocumentViewTable);
